import { BaseApiService } from '@/shared/api/BaseApiService';

export interface ProcedureCategory {
  id: string;
  organizationId: string;
  templateId: string | null;
  name: string;
  color: string;
  sortOrder: number;
  isCustom: boolean;
  isActive: boolean;
}

export interface ProcedureCategoryWithCount extends ProcedureCategory {
  count: number;
}

export interface ProcedureCatalogStep {
  id: string;
  organizationId: string;
  procedureCatalogItemId: string;
  templateStepId: string | null;
  name: string;
  description: string | null;
  feePaise: number;
  sessionOrder: number;
  typicalDuration: string | null;
  taxRateBps?: number | null;
  durationMinutes?: number | null;
  minGapDaysFromPrev?: number | null;
  maxGapDaysFromPrev?: number | null;
  dependsOnStepId?: string | null;
  requiresDentist?: boolean;
  isOptional?: boolean;
}

export interface ProcedureCatalogItem {
  id: string;
  organizationId: string;
  templateId: string | null;
  categoryId: string;
  code: string;
  name: string;
  icdCode: string;
  description: string;
  duration: string;
  sessions: string;
  billingGranularity: 'per_step' | 'whole';
  toothSelectionMode: 'none' | 'per_tooth' | 'optional';
  defaultTaxRateBps: number;
  insuranceCovered: string;
  isActive: boolean;
  isCustom: boolean;
  sortOrder: number;
  timesUsed: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  totalFeePaise?: number;
  stepCount?: number;
  steps?: ProcedureCatalogStep[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
}

export interface ListCatalogParams {
  categoryId?: string;
  search?: string;
  sortBy?: 'name' | 'duration' | 'times_used' | 'sort_order' | 'created_at';
  sortDir?: 'asc' | 'desc';
  page?: number;
  limit?: number;
  isActive?: boolean;
}

export interface CreateCatalogItemInput {
  categoryId: string;
  code?: string;
  name: string;
  icdCode?: string;
  description?: string;
  duration?: string;
  sessions?: string;
  billingGranularity?: 'per_step' | 'whole';
  toothSelectionMode?: 'none' | 'per_tooth' | 'optional';
  defaultTaxRateBps?: number;
  insuranceCovered?: 'yes' | 'no' | 'partial';
  sortOrder?: number;
}

export interface UpdateCatalogItemInput {
  name?: string;
  code?: string;
  categoryId?: string;
  icdCode?: string;
  description?: string;
  duration?: string;
  sessions?: string;
  billingGranularity?: 'per_step' | 'whole';
  toothSelectionMode?: 'none' | 'per_tooth' | 'optional';
  defaultTaxRateBps?: number;
  insuranceCovered?: 'yes' | 'no' | 'partial';
  isActive?: boolean;
  sortOrder?: number;
}

export interface CreateCategoryInput {
  name: string;
  color: string;
  sortOrder?: number;
}

export interface UpdateCategoryInput {
  name?: string;
  color?: string;
  sortOrder?: number;
  isActive?: boolean;
}

export interface CreateCatalogStepInput {
  name: string;
  description?: string;
  feePaise: number;
  sessionOrder?: number;
  typicalDuration?: string;
  taxRateBps?: number | null;
  durationMinutes?: number | null;
  minGapDaysFromPrev?: number | null;
  maxGapDaysFromPrev?: number | null;
  dependsOnStepId?: string | null;
  requiresDentist?: boolean;
  isOptional?: boolean;
}

export interface UpdateCatalogStepInput {
  name?: string;
  description?: string;
  feePaise?: number;
  sessionOrder?: number;
  typicalDuration?: string;
  taxRateBps?: number | null;
  durationMinutes?: number | null;
  minGapDaysFromPrev?: number | null;
  maxGapDaysFromPrev?: number | null;
  dependsOnStepId?: string | null;
  requiresDentist?: boolean;
  isOptional?: boolean;
}

export interface CatalogItemWithSteps extends ProcedureCatalogItem {
  steps: ProcedureCatalogStep[];
}

export class ProcedureCatalogService extends BaseApiService {
  protected readonly resourcePath = 'procedure-catalog';

  async list(params: ListCatalogParams = {}): Promise<PaginatedResponse<ProcedureCatalogItem>> {
    return this.get('', { params });
  }

  async getById(id: string): Promise<CatalogItemWithSteps> {
    return this.get(`/${id}`);
  }

  async create(input: CreateCatalogItemInput): Promise<ProcedureCatalogItem> {
    return this.post('', input);
  }

  async update(id: string, input: UpdateCatalogItemInput): Promise<ProcedureCatalogItem> {
    return this.patch(`/${id}`, input);
  }

  async remove(id: string): Promise<void> {
    return super.delete(`/${id}`);
  }

  async getCategories(): Promise<ProcedureCategoryWithCount[]> {
    return this.get('/categories');
  }

  async createCategory(input: CreateCategoryInput): Promise<ProcedureCategory> {
    return this.post('/categories', input);
  }

  async updateCategory(id: string, input: UpdateCategoryInput): Promise<ProcedureCategory> {
    return this.patch(`/categories/${id}`, input);
  }

  async deleteCategory(id: string): Promise<void> {
    return this.delete(`/categories/${id}`);
  }

  async createStep(
    itemId: string,
    input: CreateCatalogStepInput,
  ): Promise<ProcedureCatalogStep> {
    return this.post(`/${itemId}/steps`, input);
  }

  async updateStep(
    itemId: string,
    stepId: string,
    input: UpdateCatalogStepInput,
  ): Promise<ProcedureCatalogStep> {
    return this.patch(`/${itemId}/steps/${stepId}`, input);
  }
}
